import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import {
  SchoolsCrossLink, SchoolsHero, SchoolsPain, SchoolsAnswer, SchoolsLanguages,
  SchoolsHow, SchoolsBuilt, SchoolsMethod, SchoolsProof, SchoolsSample,
  SchoolsFAQ, SchoolsFooterCTA,
} from '../components/Schools.jsx';

/* Schools — Languages & EAL (route /schools2).
 *
 * Copy ported from the design handoff's schools.html inline
 * TWEAK_DEFAULTS. Shared chrome keys the Header/Footer read
 * (navBlog, login, footerAbout… etc.) were not present in the HTML;
 * sourced from routes/TeachersOnline.jsx's TWEAK_DEFAULTS so the
 * shared Header/Footer render fully on this route too.
 */
const TWEAK_DEFAULTS = {
  // --- shared chrome (Header) ---
  navWelshResources: 'Welsh Resources',
  navCommunity: 'Community',
  navAboutUs: 'About us',
  navBlog: 'Blog',           // not in HTML — from TeachersOnline.jsx
  navContact: 'Contact',
  login: 'Login',            // not in HTML — from TeachersOnline.jsx
  startLearning: 'Start Learning',

  // --- account-setup destination (MFL/EAL → /schools2) ---
  scSetupUrl: 'https://www.saysomethingin.app/schools2',

  // --- cross-link bar (points at the Welsh & Irish page) ---
  scCrossText: 'Teaching Welsh or Irish?',
  scCrossCta: 'See our Welsh & Irish schools page',
  scCrossHref: '/schools1',

  // --- hero ---
  scKicker: 'For schools & teachers',
  scHeadline: 'Every pupil speaking, from the very first lesson.',
  scSub: "SaySomethingin gets your pupils talking – Welsh, Spanish, French and more – with five-minute speaking practice they actually do. Self-paced for every ability, with a dashboard that shows you who's moving.",
  scCta: 'Set up your school',
  scCta2: 'See how it works',
  scTrust: '30 days free on every language. No pupil card details, ever – on the Chromebooks and tablets you already have.',
  scBadgeBig: '5 min / day',
  scBadgeLabel: 'is enough to move the needle – in an independent Cardiff University study.',

  // --- pain ---
  scPainKicker: "Let's be honest",
  scPainTitle: 'Pupils can pass the test and still not speak.',
  scPainSub: 'Speaking is the hardest thing to teach at scale – and the first thing squeezed out of a busy timetable.',
  scPainIntro: 'Sound familiar?',
  scPain1: 'Thirty in the class, and barely a minute of speaking time each.',
  scPain2: 'The confident ones race ahead; the rest go quiet.',
  scPain3: 'They learn it for the test and forget it by half term.',
  scPain4: "I spend my evenings making resources I'll use once.",
  scPain5: "Our new arrivals need English, and I can't teach in twelve languages.",
  scPain6: "Uptake keeps falling and I can't make the case on my own.",

  // --- the shift (answer) ---
  scAnsKicker: "Here's the shift",
  scAnsTitle: 'Daily speaking practice that runs itself – so your lessons do the part only you can.',
  scAnsLede: 'Pupils practise out loud for a few minutes every day, at their own level. They arrive to your lesson warmed up and confident, and you get the time back for conversation, correction and culture.',
  scAnsTag1: 'Every ability',
  scAnsT1: 'Self-paced, so no one is left behind.',
  scAnsB1: 'Each pupil moves at their own speed – the quick ones push on, the quieter ones build confidence without an audience. One classroom, every level, all progressing.',
  scAnsTag2: 'Speaking first',
  scAnsT2: 'They talk from minute one.',
  scAnsB2: "No vocab lists, no worksheets. Audio prompts, thinking time, and speaking out loud – the fastest route to a pupil who'll actually open their mouth in your lesson.",
  scAnsTag3: 'You can see it',
  scAnsT3: "A dashboard that shows who's moving.",
  scAnsB3: 'Track practice, belts and progress by class and by pupil. Spot who needs a nudge, celebrate who\'s flying, and play a session together on the board.',
  scAnsCloser: 'Same timetable. Same teacher. Pupils who turn up ready to speak.',

  // --- languages ---
  scLangKicker: 'Across the curriculum',
  scLangTitle: 'The languages your school teaches – and the one your new arrivals need.',
  scLangLede: 'Welsh-medium and immersion, modern foreign languages, and English as an additional language for EAL pupils – all in one method, all on one dashboard.',
  scLangChipsLabel: 'Available now',
  scLangPoint1H: 'Built for Welsh-medium & immersion',
  scLangPoint1B: 'SaySomethingin started with Welsh. Get pupils speaking outside the classroom, not just inside it.',
  scLangPoint2H: 'Modern foreign languages that stick',
  scLangPoint2B: 'Spanish, French, German and more – speaking-first practice that finally turns vocab lists into real conversation.',
  scLangPoint3H: 'English for new arrivals (EAL)',
  scLangPoint3B: 'Pupils learn English in their own first language, so absolute beginners arrive already speaking – without you teaching in twelve languages at once.',
  scLangWelsh: 'Welsh',
  scLangIrish: 'Irish',
  scLangSpanish: 'Spanish',
  scLangFrench: 'French',
  scLangGerman: 'German',
  scLangItalian: 'Italian',
  scLangPortuguese: 'Portuguese',
  scLangMandarin: 'Mandarin',
  scLangJapanese: 'Japanese',
  scLangKorean: 'Korean',
  scLangArabic: 'Arabic',

  // --- how it works ---
  scHowKicker: 'How it works',
  scHowTitle: 'One method, three places it works.',
  scHowATag: 'In the classroom',
  scHowATitle: 'Play as a class',
  scHowA1: 'Run a session together on the whiteboard, or have pupils follow on their own devices.',
  scHowA2: 'Use it as a five-minute starter, a full lesson, or cover work that teaches itself.',
  scHowA3: 'Works on Chromebooks, tablets or phones – gather round one screen or one each.',
  scHowBTag: 'Between lessons',
  scHowBTitle: 'Practice that gets done',
  scHowB1: 'Set a few minutes of speaking practice as homework pupils can actually keep up with.',
  scHowB2: 'Self-paced, so every pupil is stretched at the right level.',
  scHowB3: 'They arrive to your next lesson warmer, faster, and more willing to speak.',
  scHowCTag: 'For the school',
  scHowCTitle: 'See the whole picture',
  scHowC1: 'Class and pupil dashboards – progress, belts, and time on task at a glance.',
  scHowC2: 'Join codes let pupils enrol themselves; no spreadsheets to maintain.',
  scHowC3: 'Roll it out across a department, a key stage, or the whole school.',
  scHowPitch: 'Set it up in an afternoon. See pupils speaking by the end of the week.',

  // --- built by teachers ---
  scBuiltKicker: 'Built by teachers',
  scBuiltTitle: "Shaped by people who've been at the front of the room.",
  scBuiltBody: 'SaySomethingin for schools has been developed with Catrin, Tom and Aran – teachers with over 50 years of classroom experience between them. From the lesson flow to the way pupils practise between sessions, every part was designed by people who\'ve lived it.',
  scBuiltName1: 'Catrin',
  scBuiltRole1: 'English & Welsh teacher',
  scBuiltName2: 'Tom',
  scBuiltRole2: 'Physics & Maths teacher',
  scBuiltName3: 'Aran',
  scBuiltRole3: 'English & Welsh teacher',

  // --- method ---
  scMethodKicker: 'The method',
  scMethodTitle: "Why it works when so much else hasn't.",
  scMethodLede: "SaySomethingin is a speaking-first method. It's how celebrities prepare for high-pressure interviews in another language – and in pilots it moves the needle in as little as five minutes a week.",
  scMethod1Title: 'Speaking from minute one',
  scMethod1Body: 'No reading, no vocab lists. Just audio prompts, thinking time, and speaking out loud – the fastest path to real confidence.',
  scMethod2Title: 'No streaks, no shame',
  scMethod2Body: 'No owls, no guilt loops. Just high-intensity speaking practice pupils can respect – and that respects their time.',
  scMethod3Title: 'It complements your teaching',
  scMethod3Body: 'The method builds the reflexes. You build the conversation, the nuance, the culture. Neither one replaces the other.',
  scMethodMore: 'Read more about the methodology',

  // --- proof ---
  scProofKicker: 'Proof',
  scProofTitle: 'Teachers, and the research.',
  scProofQuoteA: 'Our Spanish uptake was sliding. The pupils practise the speaking at home now, so lessons are finally about talking – and the numbers are back up.',
  scProofNameA: 'Sioned R.',
  scProofRoleA: 'Head of Languages – secondary school',
  scProofQuoteB: 'I set it as a five-minute starter and got my lessons back. The drilling happens at home; we spend the lesson actually talking.',
  scProofNameB: 'Huw E.',
  scProofRoleB: 'MFL teacher – comprehensive',
  scProofQuoteC: 'Our EAL new arrivals went from silent to volunteering answers in a few weeks. It teaches them in their own language first.',
  scProofNameC: 'Laura M.',
  scProofRoleC: 'EAL co-ordinator – primary',
  scResearchKicker: 'Independent Cardiff University study',
  scResearchTitle: 'It works. Even at five minutes a week.',
  scResearchBody: "An independent 2024 evaluation by Cardiff University PhD researcher Peter Russell found that as little as five minutes a week with SaySomethingin produced significantly improved results – where considerably more time on other approaches wasn't moving the needle. The study also tracked engagement, retention, and some unexpected ways less confident learners improved.",
  scResearchLink: 'Read the full report',
  scResearchPull: 'Some of the most successful learners with SaySomethingin are not the learners usually recognised as academically strong.',
  scResearchPullCite: '– Cardiff University report, 2024',

  // --- sample & pricing ---
  scSampleKicker: 'Try it yourself',
  scSampleTitle: 'Feel the method before your pupils do.',
  scSampleLede: "Pick a language, press play, and learn for up to half an hour. You'll know in two minutes exactly what your pupils will experience.",
  scSamplePickerLabel: 'Choose a language',
  scPricingKicker: 'Pricing',
  scPricingTitle: 'Try everything, then pay per teacher.',
  scPricingLede: 'Start with a 30-day free trial of any language. Keep going for £15 per teacher a month – every language included.',
  scPricingOfferBig: '30 days free',
  scPricingOfferSub: 'every language, no card to start',
  scPricingLine1Label: 'Then, per teacher',
  scPricingLine1Val: '£15 / mo',
  scPricingLine2Label: 'Pupil accounts',
  scPricingLine2Val: '£5 / mo',
  scPricingFeat1: 'Every language – Welsh, Irish, Spanish, French and 7 more, plus EAL English',
  scPricingFeat2: '£150 / year per teacher if you pay annually',
  scPricingFeat3: 'Up to 10 classes per teacher',
  scPricingFeat4: 'Class & pupil dashboards – no pupil card details, ever',
  scPricingCta: 'Start your 30-day trial',
  scPricingNote: 'Teaching Welsh or Irish? Those courses are free for a full year. Whole-school & MAT pricing on request.',

  // --- FAQ ---
  scFaqKicker: 'School questions',
  scFaqTitle: 'The things you actually need to know.',
  scFaqLede: "Money, data, devices, and how pupils get on board. If we've missed something, drop us a line and we'll come back within a working day.",
  scFaqContact: 'Email the schools team →',
  scFaqQ1: 'How do pupils join?',
  scFaqA1: "Each class gets a join code. Pupils enter it once and they're enrolled – no spreadsheets, no manual setup. Teachers and admins get their own codes too.",
  scFaqQ2: 'What about pupil data and GDPR?',
  scFaqA2: "We collect the minimum we need to run the learning, store it securely, and never sell it. We're happy to sign a data-processing agreement, and pupils never need to give card details.",
  scFaqQ3: 'What devices does it need?',
  scFaqA3: 'Whatever you already have – Chromebooks, tablets, phones or the classroom computer. Cast to the board for a whole-class session, or have pupils work one-to-a-device. No special kit.',
  scFaqQ4: 'Which languages are included?',
  scFaqA4: 'Welsh, Irish, Spanish, French, German, Italian, Portuguese, Mandarin, Japanese, Korean and Arabic – plus English as an additional language for EAL pupils. One setup covers them all.',
  scFaqQ5: 'Does it replace the teacher?',
  scFaqA5: 'Never. It does the repetition and drilling so you can spend lesson time on conversation, correction and culture – the things only you can do.',
  scFaqQ6: 'How does the teacher dashboard work?',
  scFaqA6: "You see practice, progress and belts by class and by pupil, so you can spot who needs a nudge and who's ready to be stretched. You can also play a session together on the board.",
  scFaqQ7: 'Can we start small?',
  scFaqA7: "Yes. One teacher can set up a single class and invite pupils with a code. When you're ready, add more classes, more teachers, or roll it out across the school.",
  scFaqQ8: 'How do we pay?',
  scFaqA8: 'Start with a 30-day free trial of any language. After that it\'s £15 per teacher a month (or £150 a year), which unlocks every language; pupil accounts are £5 a month and each teacher can run up to 10 classes. Welsh, Irish and our free courses are free for a full year – see our Welsh & Irish schools page. Whole-school and MAT pricing on request.',

  // --- footer CTA ---
  scFooterCtaTitle: 'Get your pupils speaking this term.',
  scFooterCtaSub: 'Set up in an afternoon, invite a class with a join code, and watch pupils arrive to your lessons ready to talk. Start with one class or your whole school.',
  scFooterCtaBtn: 'Set up your school',
  scFooterCtaBtn2: 'Talk to the schools team',
  scFooterCtaNote: '30-day free trial, no pupil card details. Whole-school and MAT pricing on request.',

  // --- shared chrome (Footer) ---
  footerCol1: 'The product',
  footerCol2: 'Community',
  // keys below not in HTML — from TeachersOnline.jsx
  footerCol3: 'Download',
  footerAbout: 'About',
  footerCareers: 'Careers',
  footerContact: 'Contact',
  footerHelp: 'Help centre',
  footerTerms: 'Terms',
  footerPrivacy: 'Privacy',
  footerAppHeading: 'Download',
  footerAppApple: 'iOS',
  footerAppGoogle: 'Android',
  footerLivingWage: 'We are a Living Wage Employer',
  footerCopyright: '© 2025 SaySomethingin Cyf',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Cross-link bar', keys: ['scCrossText','scCrossCta','scCrossHref'], long:[] },
  { name: 'Hero', keys: ['scKicker','scHeadline','scSub','scCta','scCta2','scTrust','scBadgeBig','scBadgeLabel'], long:['scHeadline','scSub','scBadgeLabel'] },
  { name: 'Pain recognition', keys: ['scPainKicker','scPainTitle','scPainSub','scPainIntro','scPain1','scPain2','scPain3','scPain4','scPain5','scPain6'], long:['scPainTitle','scPainSub','scPain1','scPain2','scPain3','scPain4','scPain5','scPain6'] },
  { name: 'The shift', keys: ['scAnsKicker','scAnsTitle','scAnsLede','scAnsTag1','scAnsT1','scAnsB1','scAnsTag2','scAnsT2','scAnsB2','scAnsTag3','scAnsT3','scAnsB3','scAnsCloser'], long:['scAnsTitle','scAnsLede','scAnsB1','scAnsB2','scAnsB3','scAnsCloser'] },
  { name: 'Languages', keys: ['scLangKicker','scLangTitle','scLangLede','scLangChipsLabel','scLangPoint1H','scLangPoint1B','scLangPoint2H','scLangPoint2B','scLangPoint3H','scLangPoint3B','scLangWelsh','scLangIrish','scLangSpanish','scLangFrench','scLangGerman','scLangItalian','scLangPortuguese','scLangMandarin','scLangJapanese','scLangKorean','scLangArabic'], long:['scLangTitle','scLangLede','scLangPoint1B','scLangPoint2B','scLangPoint3B'] },
  { name: 'How it works', keys: ['scHowKicker','scHowTitle','scHowATag','scHowATitle','scHowA1','scHowA2','scHowA3','scHowBTag','scHowBTitle','scHowB1','scHowB2','scHowB3','scHowCTag','scHowCTitle','scHowC1','scHowC2','scHowC3','scHowPitch'], long:['scHowA1','scHowA2','scHowA3','scHowB1','scHowB2','scHowB3','scHowC1','scHowC2','scHowC3','scHowPitch'] },
  { name: 'Built by teachers', keys: ['scBuiltKicker','scBuiltTitle','scBuiltBody','scBuiltName1','scBuiltRole1','scBuiltName2','scBuiltRole2','scBuiltName3','scBuiltRole3'], long:['scBuiltTitle','scBuiltBody'] },
  { name: 'Method', keys: ['scMethodKicker','scMethodTitle','scMethodLede','scMethod1Title','scMethod1Body','scMethod2Title','scMethod2Body','scMethod3Title','scMethod3Body','scMethodMore'], long:['scMethodTitle','scMethodLede','scMethod1Body','scMethod2Body','scMethod3Body'] },
  { name: 'Proof', keys: ['scProofKicker','scProofTitle','scProofQuoteA','scProofNameA','scProofRoleA','scProofQuoteB','scProofNameB','scProofRoleB','scProofQuoteC','scProofNameC','scProofRoleC','scResearchKicker','scResearchTitle','scResearchBody','scResearchLink','scResearchPull','scResearchPullCite'], long:['scProofQuoteA','scProofQuoteB','scProofQuoteC','scResearchBody','scResearchPull'] },
  { name: 'Sample & pricing', keys: ['scSampleKicker','scSampleTitle','scSampleLede','scSamplePickerLabel','scPricingKicker','scPricingTitle','scPricingLede','scPricingOfferBig','scPricingOfferSub','scPricingLine1Label','scPricingLine1Val','scPricingLine2Label','scPricingLine2Val','scPricingFeat1','scPricingFeat2','scPricingFeat3','scPricingFeat4','scPricingCta','scPricingNote'], long:['scSampleTitle','scSampleLede','scPricingLede','scPricingFeat1','scPricingNote'] },
  { name: 'FAQ', keys: ['scFaqKicker','scFaqTitle','scFaqLede','scFaqContact','scFaqQ1','scFaqA1','scFaqQ2','scFaqA2','scFaqQ3','scFaqA3','scFaqQ4','scFaqA4','scFaqQ5','scFaqA5','scFaqQ6','scFaqA6','scFaqQ7','scFaqA7','scFaqQ8','scFaqA8'], long:['scFaqLede','scFaqA1','scFaqA2','scFaqA3','scFaqA4','scFaqA5','scFaqA6','scFaqA7','scFaqA8'] },
  { name: 'Footer CTA', keys: ['scFooterCtaTitle','scFooterCtaSub','scFooterCtaBtn','scFooterCtaBtn2','scFooterCtaNote'], long:['scFooterCtaTitle','scFooterCtaSub','scFooterCtaNote'] },
];

export default function SchoolsLanguagesPage() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Schools — Languages & EAL">
      <Header t={tweaks}/>
      <SchoolsCrossLink t={tweaks}/>
      <main>
        <SchoolsHero t={tweaks}/>
        <SchoolsPain t={tweaks}/>
        <SchoolsAnswer t={tweaks}/>
        <SchoolsLanguages t={tweaks}/>
        <SchoolsHow t={tweaks}/>
        <SchoolsBuilt t={tweaks}/>
        <SchoolsMethod t={tweaks}/>
        <SchoolsProof t={tweaks}/>
        <SchoolsSample t={tweaks}/>
        <SchoolsFAQ t={tweaks}/>
        <SchoolsFooterCTA t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
